import { useMemo, useState } from 'react';
import { History, ImageOff, Search } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import type { StoreProduct, StorePriceUpdate } from '@/client/types/stores/parity';
import { useStorePriceHistoryQuery, useStoreProductsQuery } from '@/client/api/stores/parity.query';
import { useBulkUpdateStorePrices } from '@/client/api/stores/parity.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { Pager, SectionCard, label, money, shortDate, TableSkeleton, InfoRowsSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';

const LIMIT = 25;

/** ff-api `service.store_pricing.PRICING_TAX_CATEGORIES`. */
const TAX_CATEGORIES = [
  'standard', 'zero_rated', 'exempt', 'special', 'digital_service', 'telecom', 'excise', 'imported',
];

type Draft = { price?: string; price_old?: string; cost?: string; tax_category?: string };

const current = (p: StoreProduct) => p.prices.find((x) => (x.ccode ?? 'GHS') === 'GHS') ?? p.prices[0];
const str = (n: number | null | undefined) => (n == null ? '' : String(n));
const coverUrl = (p: StoreProduct) => p.cover?.public_url_cf || p.cover?.public_url || p.cover?.public_url_uc || undefined;

/** Turn edited cells into `prices/bulk` updates (only changed fields). */
const toUpdates = (products: StoreProduct[], drafts: Record<number, Draft>): StorePriceUpdate[] =>
  Object.entries(drafts).flatMap(([eidStr, d]) => {
    const p = products.find((x) => x.eid === Number(eidStr));
    if (!p) return [];
    const pr = current(p);
    const u: StorePriceUpdate = { eid: p.eid, ccode: pr?.ccode ?? 'GHS' };
    if (d.price !== undefined && d.price !== str(pr?.price)) u.price = Number(d.price);
    if (d.price_old !== undefined && d.price_old !== str(pr?.price_old)) u.price_old = d.price_old ? Number(d.price_old) : null;
    if (d.cost !== undefined && d.cost !== str(pr?.cost)) u.cost = d.cost ? Number(d.cost) : null;
    if (d.tax_category !== undefined && d.tax_category !== (p.tax_category ?? '')) u.tax_category = d.tax_category || null;
    return Object.keys(u).length > 2 ? [u] : [];
  });

const HistoryDialog: React.FC<{ storeid: number; product: StoreProduct; onClose: () => void }> = ({ storeid, product, onClose }) => {
  const { data, isLoading } = useStorePriceHistoryQuery(storeid, product.eid);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Price history · {product.name_marketing ?? `#${product.eid}`}</DialogTitle>
          <DialogDescription>
            {data
              ? `${data.summary.change_count} changes · lowest ${money(data.summary.lowest)} · highest ${money(data.summary.highest)}`
              : 'Loading…'}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <InfoRowsSkeleton rows={6} />
        ) : !data?.history.length ? (
          <p className="py-6 text-center text-sm text-gray-400">No price changes recorded.</p>
        ) : (
          <div className="max-h-80 divide-y divide-gray-100 overflow-y-auto text-sm">
            {data.history.map((h) => (
              <div key={h.id} className="flex items-center justify-between py-2">
                <span className="text-gray-600">{shortDate(h.date_added)}</span>
                <span className="tabular-nums font-medium">{money(h.price)}</span>
                <span className="text-xs text-gray-400">{h.uid ? `by uid ${h.uid}` : ''}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Listings with active prices and tax settings (`GET /<id>/products`). Edits
 * are collected and applied in one `POST /prices/bulk` (`admin.store.pricing`),
 * the seller's bulk editor; each change lands in price history with the admin
 * as actor.
 */
export const PricingTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [confirm, setConfirm] = useState(false);
  const [historyFor, setHistoryFor] = useState<StoreProduct | null>(null);
  const canEdit = useGate(PERMISSIONS.store.pricing).allowed;
  const bulk = useBulkUpdateStorePrices();

  const { data, isLoading, isFetching } = useStoreProductsQuery(store.storeid, {
    search: search.trim() || undefined, limit: LIMIT, offset,
  });
  const products = useMemo(() => data?.products ?? [], [data]);
  const updates = useMemo(() => toUpdates(products, drafts), [products, drafts]);

  const edit = (eid: number, field: keyof Draft, value: string) =>
    setDrafts((d) => ({ ...d, [eid]: { ...d[eid], [field]: value } }));
  const cell = (p: StoreProduct, field: 'price' | 'price_old' | 'cost') =>
    drafts[p.eid]?.[field] ?? str(current(p)?.[field]);

  return (
    <SectionCard
      bare
      description="Edit prices, compare-at, cost and tax, then save them together."
      actions={
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{isFetching ? 'Refreshing…' : `${data?.total_count ?? 0} listings`}</span>
          {updates.length > 0 && (
            <>
              <button type="button" onClick={() => setDrafts({})} className="text-xs text-gray-500 hover:text-gray-800">Discard</button>
              <PermissionButton code={PERMISSIONS.store.pricing} size="sm" onClick={() => setConfirm(true)}>
                Save {updates.length} change{updates.length === 1 ? '' : 's'}
              </PermissionButton>
            </>
          )}
        </div>
      }
    >
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setOffset(0); }} placeholder="Search listings" className="pl-8" />
      </div>

      {isLoading ? (
        <TableSkeleton cols={6} />
      ) : products.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">No listings match.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-400">
                <th className="py-2 pr-3">Listing</th>
                <th className="py-2 pr-3">Price</th>
                <th className="py-2 pr-3">Compare-at</th>
                <th className="py-2 pr-3">Cost</th>
                <th className="py-2 pr-3">Tax</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.map((p) => {
                const img = coverUrl(p);
                const dirty = updates.some((u) => u.eid === p.eid);
                return (
                  <tr key={p.eid} className={dirty ? 'bg-teal-50/50' : undefined}>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100">
                          {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : <ImageOff className="h-4 w-4 text-gray-300" />}
                        </div>
                        <span className="max-w-[200px] truncate font-medium text-gray-900">{p.name_marketing ?? `#${p.eid}`}</span>
                      </div>
                    </td>
                    {(['price', 'price_old', 'cost'] as const).map((f) => (
                      <td key={f} className="py-2 pr-3">
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          disabled={!canEdit}
                          value={cell(p, f)}
                          onChange={(e) => edit(p.eid, f, e.target.value)}
                          className="h-8 w-24"
                        />
                      </td>
                    ))}
                    <td className="py-2 pr-3">
                      <select
                        disabled={!canEdit}
                        value={drafts[p.eid]?.tax_category ?? p.tax_category ?? ''}
                        onChange={(e) => edit(p.eid, 'tax_category', e.target.value)}
                        className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs"
                        title={p.tax_source ? `Tax from: ${label(p.tax_source)}` : undefined}
                      >
                        <option value="">Inherit</option>
                        {TAX_CATEGORIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
                      </select>
                    </td>
                    <td className="py-2 text-right">
                      <button type="button" onClick={() => setHistoryFor(p)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Price history">
                        <History className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pager total={data?.total_count ?? 0} limit={LIMIT} offset={offset} onChange={(o) => { setOffset(o); }} />

      {confirm && (
        <ReasonDialog
          open
          title={`Save ${updates.length} price change${updates.length === 1 ? '' : 's'}`}
          description="Applied to the seller's listings now; buyers see new prices immediately. Invalid rows are skipped and reported."
          confirmLabel="Save prices"
          reasonRequired
          loading={bulk.isPending}
          onConfirm={(reason) =>
            bulk.mutate(
              { storeid: store.storeid, updates, reason },
              { onSuccess: () => { setConfirm(false); setDrafts({}); } },
            )
          }
          onClose={() => setConfirm(false)}
        />
      )}
      {historyFor && <HistoryDialog storeid={store.storeid} product={historyFor} onClose={() => setHistoryFor(null)} />}
    </SectionCard>
  );
};
